import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  MenuItem,
  Alert,
  CircularProgress
} from '@mui/material';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';
import { planStatusLabel } from './planConstants';

const TENANT_STATUSES = ['pending', 'trial', 'active', 'suspended', 'archived'];

const empty = {
  tenant_name: '',
  tenant_code: '',
  status: 'pending',
  primary_contact_name: '',
  primary_contact_email: '',
  primary_contact_phone: '',
  domain: '',
  subdomain: '',
  timezone: 'UTC',
  country: '',
  billing_email: '',
  plan_id: '',
  trial_ends_at: ''
};

function toInputDateTimeLocal(v) {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function PlatformTenantFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id) && id !== 'new';
  const navigate = useNavigate();
  const { user, ready } = usePlatformAuth();

  const [form, setForm] = useState(empty);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await platformApiFetch('/api/platform/plans?pageSize=200&sort=sort_order&order=asc&includeDeleted=1');
        const json = await res.json().catch(() => ({}));
        if (res.ok && !cancelled) setPlans(json.plans || []);
      } catch (_e) {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/tenants/${id}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load tenant');
      const t = json.tenant;
      setForm({
        tenant_name: t.tenant_name || '',
        tenant_code: t.tenant_code || '',
        status: t.status || 'pending',
        primary_contact_name: t.primary_contact_name || '',
        primary_contact_email: t.primary_contact_email || '',
        primary_contact_phone: t.primary_contact_phone || '',
        domain: t.domain || '',
        subdomain: t.subdomain || '',
        timezone: t.timezone || 'UTC',
        country: t.country || '',
        billing_email: t.billing_email || '',
        plan_id: t.plan_id != null ? String(t.plan_id) : '',
        trial_ends_at: toInputDateTimeLocal(t.trial_ends_at)
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id, isEdit]);

  useEffect(() => {
    if (ready && user?.role !== 'super_admin') {
      navigate('/platform/tenants', { replace: true });
    }
  }, [ready, user, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const body = {
      tenant_name: form.tenant_name.trim(),
      primary_contact_name: form.primary_contact_name.trim() || null,
      primary_contact_email: form.primary_contact_email.trim() || null,
      primary_contact_phone: form.primary_contact_phone.trim() || null,
      domain: form.domain.trim().toLowerCase() || null,
      subdomain: form.subdomain.trim().toLowerCase() || null,
      timezone: form.timezone.trim() || 'UTC',
      country: form.country.trim().toUpperCase().slice(0, 2) || null,
      billing_email: form.billing_email.trim() || null,
      plan_id: form.plan_id ? Number(form.plan_id) : null,
      trial_ends_at: form.trial_ends_at ? new Date(form.trial_ends_at).toISOString() : null
    };
    if (!isEdit) {
      body.tenant_code = form.tenant_code.trim().toLowerCase();
      body.status = form.status;
    }
    try {
      const res = await platformApiFetch(
        isEdit ? `/api/platform/tenants/${id}` : '/api/platform/tenants',
        { method: isEdit ? 'PATCH' : 'POST', body }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Save failed');
      navigate(`/platform/tenants/${json.tenant.id}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!ready || user?.role !== 'super_admin') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit tenant' : 'Create tenant'}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Paper component="form" onSubmit={submit} sx={{ p: 3, maxWidth: 720 }}>
        <TextField
          label="Tenant name"
          value={form.tenant_name}
          onChange={(e) => set('tenant_name', e.target.value)}
          fullWidth
          required
          margin="normal"
        />
        <TextField
          label="Tenant code"
          value={form.tenant_code}
          onChange={(e) => set('tenant_code', e.target.value)}
          fullWidth
          required={!isEdit}
          disabled={isEdit}
          margin="normal"
          helperText={isEdit ? 'Immutable after create' : 'Used at tenant login (e.g. demo)'}
        />
        {!isEdit ? (
          <TextField
            select
            label="Status"
            value={form.status}
            onChange={(e) => set('status', e.target.value)}
            fullWidth
            margin="normal"
          >
            {TENANT_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {planStatusLabel(s)}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
            Status: <strong>{planStatusLabel(form.status)}</strong> — change from the tenant detail page.
          </Typography>
        )}
        <TextField
          label="Primary contact name"
          value={form.primary_contact_name}
          onChange={(e) => set('primary_contact_name', e.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Primary contact email"
          type="email"
          value={form.primary_contact_email}
          onChange={(e) => set('primary_contact_email', e.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Primary contact phone"
          value={form.primary_contact_phone}
          onChange={(e) => set('primary_contact_phone', e.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Domain"
          value={form.domain}
          onChange={(e) => set('domain', e.target.value)}
          fullWidth
          margin="normal"
          helperText="Optional; must be unique if set"
        />
        <TextField
          label="Subdomain"
          value={form.subdomain}
          onChange={(e) => set('subdomain', e.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Timezone"
          value={form.timezone}
          onChange={(e) => set('timezone', e.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          label="Country (ISO-2)"
          value={form.country}
          onChange={(e) => set('country', e.target.value)}
          fullWidth
          margin="normal"
          inputProps={{ maxLength: 2 }}
        />
        <TextField
          label="Billing email"
          type="email"
          value={form.billing_email}
          onChange={(e) => set('billing_email', e.target.value)}
          fullWidth
          margin="normal"
        />
        <TextField
          select
          label="Plan"
          value={form.plan_id}
          onChange={(e) => set('plan_id', e.target.value)}
          fullWidth
          margin="normal"
        >
          <MenuItem value="">None</MenuItem>
          {plans
            .filter((p) => !p.deleted_at && (p.status === 'active' || isEdit))
            .map((p) => (
              <MenuItem key={p.id} value={String(p.id)}>
                {p.name} ({p.code})
              </MenuItem>
            ))}
        </TextField>
        <TextField
          label="Trial ends"
          type="datetime-local"
          value={form.trial_ends_at}
          onChange={(e) => set('trial_ends_at', e.target.value)}
          fullWidth
          margin="normal"
          InputLabelProps={{ shrink: true }}
        />
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={22} /> : 'Save'}
          </Button>
          <Button type="button" onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
